import React, { useContext, useEffect, useState } from "react";
import {
  FaGreaterThan,
  FaMapMarkerAlt,
  FaBox,
  FaRupeeSign,
  FaUser,
  FaUserTie,
  FaCheck,
  FaEdit,
  FaImage,
  FaExternalLinkAlt,
  FaCommentDots,
} from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import AlertContext from "../../../context/Alert/AlertContext";
import axiosPrivate from "../../../hooks/axiosPrivate";
import classes from "./Order.module.css";
import LoaderContext from "../../../context/loader/LoaderContext";
import { showSuccessToast, showErrorToast } from "../../../Toster";
import ReactSelect from "react-select";

const OrderReview = () => {
  const { setIsLoading } = useContext(LoaderContext);
  const currentLogedInUserId = JSON.parse(localStorage.getItem("userDetails"));
  let { id } = useParams();

  const { setAlertMessage } = useContext(AlertContext);
  const navigate = useNavigate();

  // State variables from EditOrder.js
  const [products, setProducts] = useState([]);
  const [dealerRoles, setDealerRoles] = useState([]);
  const [areaManagerRoles, setAreaManagerRoles] = useState([]);
  const [salesRoles, setSalesRoles] = useState([]);
  const [seasonRoles, setSeasonRoles] = useState([]);
  const [logisticRoles, setLogisticRoles] = useState([]);
  const [shippingAddress, setShippingAddress] = useState([]);
  const [getData, setGetData] = useState({});
  const [selectedLogistic, setSelectedLogistic] = useState("");
  const [reviewAction, setReviewAction] = useState(""); // "approve" or "changes"
  const [changesMessage, setChangesMessage] = useState("");
  const [dealerName, setDealerName] = useState("");
  const [areaManagerName, setAreaManagerName] = useState("");
  const [salesPersonName, setSalesPersonName] = useState("");
  const [seasonName, setSeasonName] = useState("");

  // Fetch order details by ID (from EditOrder.js)
  const fetchOrderById = async (orderId) => {
    setIsLoading(true);
    try {
      const response = await axiosPrivate.get(`orders/${orderId}`);
      if (response.data.code === 200) {
        setGetData({ ...response.data.data });

        // Fetch related user names
        await fetchUserNames(response.data.data);
      }
    } catch (error) {
      showErrorToast("Failed to fetch order details");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user names for dealer, area manager, and sales person
  const fetchUserNames = async (order) => {
    try {
      // Find dealer name from dealerRoles
      const dealer = dealerRoles.find((d) => d.id == order.dealer_id);
      setDealerName(dealer?.name || "N/A");

      // Find area manager name from areaManagerRoles
      const areaManager = areaManagerRoles.find(
        (am) => am.id == order.areamanager_id,
      );
      setAreaManagerName(areaManager?.name || "N/A");

      // Find sales person name from salesRoles
      if (order.salesman_id && order.salesman_id !== "vacant") {
        const salesPerson = salesRoles.find((sp) => sp.id == order.salesman_id);
        setSalesPersonName(salesPerson?.name || "N/A");
      } else {
        setSalesPersonName("Vacant");
      }

      // Find season name from seasonRoles
      const season = seasonRoles.find((s) => s.id == order.season_id);
      setSeasonName(season?.name || "N/A");
    } catch (error) {
      console.error("Error fetching user names:", error);
    }
  };

  // Fetch all required data (from EditOrder.js)
  const fetchAllData = async () => {
    try {
      // Logistic list
      const logisticsRes = await axiosPrivate.get("users/get/logistic/manager");
      setLogisticRoles(logisticsRes?.data?.data || []);

      // Season list
      const seasonsRes = await axiosPrivate.post("seasons");
      setSeasonRoles(seasonsRes?.data?.data || []);

      // Dealer list
      const dealersRes = await axiosPrivate.post("dealers");
      setDealerRoles(dealersRes?.data?.data || []);

      // Area Manager list
      const areaManagerRes = await axiosPrivate.get("users/get/area/manager");
      setAreaManagerRoles(areaManagerRes?.data?.data || []);

      // Product list
      const productsRes = await axiosPrivate.get("stocks");
      setProducts(productsRes?.data?.data || []);
    } catch (error) {
      showErrorToast("Failed to fetch required data");
    }
  };

  // Get sales person details
  const getSalesPersonDetails = async (areamanager_id) => {
    try {
      const salesPersonRes = await axiosPrivate.get(
        `users/get/sales/person/${areamanager_id}`,
      );
      setSalesRoles(salesPersonRes?.data?.data || []);
    } catch (error) {
      console.error("Error fetching sales person details:", error);
    }
  };

  // Fetch dealer addresses
  const fetchDealerAddress = async (dealerId) => {
    if (dealerId) {
      try {
        const addressesRes = await axiosPrivate.get(
          `/users/get/addresses/${dealerId}`,
        );
        const filterAddress = addressesRes?.data?.data?.filter(
          (list) =>
            list.shipping_city && list.shipping_state && list.shipping_zip,
        );
        const dropdownOptions = filterAddress?.map((item) => {
          const parts = [
            item.shipping_address,
            item.shipping_city,
            item.shipping_state,
            item.shipping_zip,
          ].filter(Boolean);

          return {
            value: item.id,
            label: parts.join(", "),
          };
        });
        setShippingAddress(dropdownOptions || []);
      } catch (error) {
        console.error("Error fetching dealer addresses:", error);
      }
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (id) {
      fetchOrderById(id);
    }
  }, [id, dealerRoles, areaManagerRoles, seasonRoles]);

  useEffect(() => {
    if (getData.dealer_id) {
      fetchDealerAddress(getData.dealer_id);
    }
    if (getData.areamanager_id) {
      getSalesPersonDetails(getData.areamanager_id);
    }
  }, [getData]);

  useEffect(() => {
    if (
      dealerRoles.length > 0 &&
      areaManagerRoles.length > 0 &&
      seasonRoles.length > 0 &&
      getData.dealer_id
    ) {
      fetchUserNames(getData);
    }
  }, [dealerRoles, areaManagerRoles, salesRoles, seasonRoles, getData]);

  // Get product name by ID
  const getProductName = (productId) => {
    const product = products.find((p) => p.id == productId);
    return product ? product.tally_name || product.name : "Unknown Product";
  };

  // Get shipping address by ID
  const getShippingAddress = (addressId) => {
    const address = shippingAddress.find((addr) => addr.value == addressId);
    return address ? address.label : "Address not found";
  };

  // Handle order approval
  const handleApproveOrder = async () => {
    if (!selectedLogistic) {
      showErrorToast("Please select a logistics person");
      return;
    }

    setIsLoading(true);
    try {
      const formData = {
        logistic_user_id: selectedLogistic,
        order_status: "to_logistics",
        status: "approved",
      };

      await axiosPrivate.patch(`orders/status/${id}?_method=PATCH`, formData);

      // Send notification to logistics
      const notificationData = {
        title: "Order Approved and Sent to Logistics",
        order_id: id,
        user_id: selectedLogistic,
        notification_for: "logistic",
      };
      await axiosPrivate.post("/notifications/store", notificationData);

      showSuccessToast("Order approved and sent to logistics successfully");
      navigate("/admin/pending-orders");
    } catch (error) {
      showErrorToast("Failed to approve order");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle request for changes
  const handleRequestChanges = async () => {
    if (!changesMessage.trim()) {
      showErrorToast("Please provide a message for the requested changes");
      return;
    }

    setIsLoading(true);
    try {
      const formData = {
        status: "needs_changes",
        approvalRemark: changesMessage,
      };

      await axiosPrivate.patch(`orders/status/${id}?_method=PATCH`, formData);

      showSuccessToast("Change request sent successfully");
      navigate("/admin/pending-orders");
    } catch (error) {
      showErrorToast("Failed to send change request");
    } finally {
      setIsLoading(false);
    }
  };

  // Custom styles for ReactSelect
  const customStyles = {
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#767676" : "white",
      color: state.isSelected ? "white" : "black",
      "&:hover": {
        backgroundColor: state.isSelected ? "#767676" : "#e0e0e0",
      },
    }),
    control: (base, state) => ({
      ...base,
      borderColor: "#ddd",
      boxShadow: "none",
      "&:hover": {
        boxShadow: "0px 0px 2px 2px #d6d4dd",
      },
    }),
  };

  // Transform logistics data for ReactSelect
  const logisticOptions = logisticRoles.map((logistic) => ({
    value: logistic.id,
    label: logistic.name,
  }));

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                      <li className="breadcrumb-item">
                        <Link to="/admin/dashboard">Dashboard</Link>
                      </li>
                      <li className="breadcrumb-item">
                        <FaGreaterThan size={10} />
                      </li>
                      <li className="breadcrumb-item">
                        <Link to="/admin/pending-orders">Pending Orders</Link>
                      </li>
                      <li className="breadcrumb-item">
                        <FaGreaterThan size={10} />
                      </li>
                      <li className="breadcrumb-item active">Order Review</li>
                    </ol>
                  </nav>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-12">
                  {/* Header */}
                  <div
                    className={classes.dialogHeader}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: "0 0 1.5rem 0",
                    }}
                  >
                    <h3 className={classes.dialogTitle}>
                      <FaBox className="me-2" />
                      Order Review - #{getData.order_no}
                    </h3>
                  </div>

                  {/* Content */}
                  <div
                    className={classes.dialogContent}
                    style={{ padding: "0" }}
                  >
                    {/* Order Information */}
                    <div className={classes.dealerSection}>
                      <h4 className={classes.sectionTitle}>
                        Order Information
                      </h4>
                      <div className={classes.orderInfo}>
                        <div className={classes.infoItem}>
                          <FaUser className={classes.infoIcon} />
                          <div className={classes.infoDetails}>
                            <span className={classes.infoLabel}>Dealer:</span>
                            <span className={classes.infoValue}>
                              {dealerName || "No dealer selected"}
                            </span>
                          </div>
                        </div>

                        {areaManagerName && (
                          <div className={classes.infoItem}>
                            <FaUserTie className={classes.infoIcon} />
                            <div className={classes.infoDetails}>
                              <span className={classes.infoLabel}>
                                Area Manager:
                              </span>
                              <span className={classes.infoValue}>
                                {areaManagerName}
                              </span>
                            </div>
                          </div>
                        )}

                        {salesPersonName && (
                          <div className={classes.infoItem}>
                            <FaUser className={classes.infoIcon} />
                            <div className={classes.infoDetails}>
                              <span className={classes.infoLabel}>
                                Sales Person:
                              </span>
                              <span className={classes.infoValue}>
                                {salesPersonName}
                              </span>
                            </div>
                          </div>
                        )}

                        {seasonName && (
                          <div className={classes.infoItem}>
                            <FaBox className={classes.infoIcon} />
                            <div className={classes.infoDetails}>
                              <span className={classes.infoLabel}>Season:</span>
                              <span className={classes.infoValue}>
                                {seasonName}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className={classes.infoItem}>
                          <FaBox className={classes.infoIcon} />
                          <div className={classes.infoDetails}>
                            <span className={classes.infoLabel}>
                              Order Status:
                            </span>
                            <span className={classes.infoValue}>
                              <span
                                className={`badge ms-2 ${
                                  getData.order_status === "pending"
                                    ? "bg-warning"
                                    : getData.order_status === "confirmed"
                                      ? "bg-success"
                                      : getData.order_status === "to_logistics"
                                        ? "bg-info"
                                        : "bg-secondary"
                                }`}
                              >
                                {getData.order_status
                                  ?.replace("_", " ")
                                  .toUpperCase()}
                              </span>
                            </span>
                          </div>
                        </div>

                        <div className={classes.infoItem}>
                          <FaBox className={classes.infoIcon} />
                          <div className={classes.infoDetails}>
                            <span className={classes.infoLabel}>
                              Order Date:
                            </span>
                            <span className={classes.infoValue}>
                              {getData.created_at
                                ? new Date(
                                    getData.created_at,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Remarks Section */}
                    {(getData.remark || getData.area_manager_remark) && (
                      <div className={classes.dealerSection}>
                        <h4 className={classes.sectionTitle}>Remarks</h4>
                        <div className={classes.orderInfo}>
                          {getData.remark && (
                            <div className={classes.infoItem}>
                              <FaCommentDots className={classes.infoIcon} />
                              <div className={classes.infoDetails}>
                                <span className={classes.infoLabel}>
                                  Remark:
                                </span>
                                <span className={classes.infoValue}>
                                  {getData.remark}
                                </span>
                              </div>
                            </div>
                          )}

                          {getData.area_manager_remark && (
                            <div className={classes.infoItem}>
                              <FaCommentDots className={classes.infoIcon} />
                              <div className={classes.infoDetails}>
                                <span className={classes.infoLabel}>
                                  Area Manager Remark:
                                </span>
                                <span className={classes.infoValue}>
                                  {getData.area_manager_remark}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Order Image Section */}
                    {getData.image && (
                      <div className={classes.dealerSection}>
                        <h4 className={classes.sectionTitle}>Order Image</h4>
                        <div className={classes.orderInfo}>
                          <div className={classes.infoItem}>
                            <FaImage className={classes.infoIcon} />
                            <div className={classes.infoDetails}>
                              <span className={classes.infoLabel}>
                                Order Image:
                              </span>
                              <div className="mt-2">
                                <img
                                  src={getData.image}
                                  alt="Order Image"
                                  style={{
                                    maxWidth: "300px",
                                    maxHeight: "200px",
                                    width: "auto",
                                    height: "auto",
                                    borderRadius: "8px",
                                    border: "2px solid #e9ecef",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  }}
                                  onClick={() =>
                                    window.open(getData.image, "_blank")
                                  }
                                  onMouseEnter={(e) => {
                                    e.target.style.transform = "scale(1.05)";
                                    e.target.style.boxShadow =
                                      "0 4px 8px rgba(0, 0, 0, 0.2)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = "scale(1)";
                                    e.target.style.boxShadow =
                                      "0 2px 4px rgba(0, 0, 0, 0.1)";
                                  }}
                                />
                                <div className="mt-2">
                                  <small className="text-muted d-flex align-items-center">
                                    <FaExternalLinkAlt
                                      className="me-1"
                                      size={12}
                                    />
                                    Click image to open in new tab
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Products Section */}
                    <div className={classes.productsSection}>
                      <h4 className={classes.sectionTitle}>Products</h4>
                      <div className={classes.productsList}>
                        {getData?.order_items?.map((item, index) => (
                          <div key={index} className={classes.productItem}>
                            <div className={classes.productHeader}>
                              <div className={classes.productName}>
                                <FaBox className={classes.productIcon} />
                                {getProductName(item.stock_id)}
                              </div>
                              <div className={classes.productPrice}>
                                <FaRupeeSign className={classes.priceIcon} />
                                {parseFloat(item.price || 0).toFixed(2)}
                              </div>
                            </div>

                            <div className={classes.productDetails}>
                              <div className={classes.quantity}>
                                <span className={classes.label}>Quantity:</span>
                                <span className={classes.value}>
                                  {item.quantity || 0}
                                </span>
                              </div>

                              <div className={classes.subtotal}>
                                <span className={classes.label}>Subtotal:</span>
                                <span className={classes.value}>
                                  ₹
                                  {(
                                    parseFloat(item.quantity || 0) *
                                    parseFloat(item.price || 0)
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </div>

                            {/* Shipping Address */}
                            <div className={classes.shippingAddress}>
                              <FaMapMarkerAlt className={classes.addressIcon} />
                              <div className={classes.addressDetails}>
                                <span className={classes.addressLabel}>
                                  Shipping to:
                                </span>
                                <span className={classes.addressText}>
                                  {item.shipping_address_id
                                    ? getShippingAddress(
                                        item.shipping_address_id,
                                      )
                                    : "No address selected"}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Total Section */}
                    <div className={classes.totalSection}>
                      <div className={classes.totalRow}>
                        <span className={classes.totalLabel}>
                          Total Amount:
                        </span>
                        <span className={classes.totalAmount}>
                          ₹{getData?.total_amount || 0}
                        </span>
                      </div>
                    </div>

                    {/* Review Actions Section */}
                    <div className={classes.dealerSection}>
                      <h4 className={classes.sectionTitle}>Review Actions</h4>
                      <div className={classes.orderInfo}>
                        <div className="row">
                          <div className="col-12">
                            <div className="mb-3">
                              <label className="form-label fw-bold">
                                Review Action
                              </label>
                              <div className="form-check mb-2">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="reviewAction"
                                  id="approve"
                                  value="approve"
                                  checked={reviewAction === "approve"}
                                  onChange={(e) =>
                                    setReviewAction(e.target.value)
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="approve"
                                >
                                  <FaCheck className="me-2 text-success" />
                                  Approve and Send to Logistics
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="reviewAction"
                                  id="changes"
                                  value="changes"
                                  checked={reviewAction === "changes"}
                                  onChange={(e) =>
                                    setReviewAction(e.target.value)
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="changes"
                                >
                                  <FaEdit className="me-2 text-warning" />
                                  Request Changes
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {reviewAction === "approve" && (
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label fw-bold">
                                  Select Logistics Person *
                                </label>
                                <ReactSelect
                                  options={logisticOptions}
                                  styles={customStyles}
                                  placeholder="Select Logistics Person"
                                  value={logisticOptions.find(
                                    (option) =>
                                      option.value === selectedLogistic,
                                  )}
                                  onChange={(selectedOption) =>
                                    setSelectedLogistic(
                                      selectedOption?.value || "",
                                    )
                                  }
                                  isClearable
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {reviewAction === "changes" && (
                          <div className="row">
                            <div className="col-12">
                              <div className="mb-3">
                                <label className="form-label fw-bold">
                                  Changes Required Message *
                                </label>
                                <textarea
                                  className="form-control"
                                  rows="4"
                                  placeholder="Please describe what changes are needed..."
                                  value={changesMessage}
                                  onChange={(e) =>
                                    setChangesMessage(e.target.value)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer with proper spacing */}
                  <div
                    className={classes.dialogFooter}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: "1.5rem 0 0 0",
                    }}
                  >
                    <button
                      type="button"
                      className={classes.cancelButton}
                      onClick={() => navigate("/admin/pending-orders")}
                    >
                      Back to Orders
                    </button>
                    {reviewAction === "approve" && (
                      <button
                        type="button"
                        className={classes.confirmButton}
                        onClick={handleApproveOrder}
                        disabled={!selectedLogistic}
                      >
                        <FaCheck className="me-2" />
                        Approve & Send to Logistics
                      </button>
                    )}
                    {reviewAction === "changes" && (
                      <button
                        type="button"
                        className={classes.requestChangesButton}
                        onClick={handleRequestChanges}
                        disabled={!changesMessage.trim()}
                      >
                        <FaEdit className="me-2" />
                        Request Changes
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderReview;
